import React, { useEffect, useState } from "react";
import { Mail, HelpCircle, Image as ImageIcon, TrendingUp, Users, Activity, FileText, Server } from "lucide-react";
import { Panel } from "@/pages/admin/shared";
import api from "@/pages/admin/api";

export default function Overview({ onNavigate }) {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  if (!stats) return <div className="text-sm text-muted-foreground">Loading…</div>;

  const cards = [
    { label: "Total Messages", value: stats.total_contacts, icon: Mail, accent: "from-blue-500 to-blue-600" },
    { label: "Unread Messages", value: stats.unread_contacts, icon: Users, accent: "from-amber-500 to-orange-600" },
    { label: "Today's Messages", value: stats.today_contacts, icon: TrendingUp, accent: "from-emerald-500 to-emerald-600" },
    { label: "Media Files", value: stats.total_media, icon: ImageIcon, accent: "from-violet-500 to-violet-600" },
    { label: "Published FAQs", value: stats.total_faqs, icon: HelpCircle, accent: "from-pink-500 to-rose-600" },
  ];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        {cards.map((c) => (
          <div key={c.label} className="rounded-2xl bg-white dark:bg-slate-900 border border-border p-5">
            <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${c.accent} text-white grid place-items-center shadow-md mb-3`}><c.icon size={17} /></div>
            <div className="text-xs text-muted-foreground">{c.label}</div>
            <div className="font-heading text-2xl font-bold tracking-tight">{c.value}</div>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <Panel title="Recent Messages" subtitle="Latest 5 contact submissions" actions={<button onClick={() => onNavigate("messages")} className="text-xs text-blue-600 hover:underline">View all →</button>}>
          {stats.recent_contacts.length === 0 && <div className="text-sm text-muted-foreground py-4">No messages yet.</div>}
          <div className="space-y-3">
            {stats.recent_contacts.map((c) => (
              <div key={c.id} className="flex items-start gap-3 rounded-xl border border-border p-3">
                <span className="w-9 h-9 rounded-full bg-blue-50 dark:bg-blue-950/40 text-blue-600 grid place-items-center shrink-0 text-xs font-bold">
                  {(c.name || "?").charAt(0).toUpperCase()}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{c.name} <span className="text-muted-foreground">· {c.email}</span></div>
                  <div className="text-xs text-muted-foreground line-clamp-1">{c.message}</div>
                </div>
                <span className={`text-[10px] px-2 py-0.5 rounded-full ${c.reply_status === "unread" ? "bg-blue-100 text-blue-700" : c.reply_status === "replied" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>{c.reply_status}</span>
              </div>
            ))}
          </div>
        </Panel>

        <Panel title="System Status" subtitle="API and content health">
          <div className="space-y-3 text-sm">
            <div className="flex items-center justify-between"><span className="flex items-center gap-2"><Server size={13} className="text-emerald-500" /> API</span><span className="text-emerald-600 text-xs">Operational</span></div>
            <div className="flex items-center justify-between"><span className="flex items-center gap-2"><FileText size={13} className="text-blue-500" /> Content DB</span><span className="text-emerald-600 text-xs">Connected</span></div>
            <div className="flex items-center justify-between"><span className="flex items-center gap-2"><Users size={13} className="text-violet-500" /> Session</span><span className="text-emerald-600 text-xs">Active</span></div>
            <div className="text-[10px] text-muted-foreground pt-3 border-t border-border">Server time: {new Date(stats.server_time).toLocaleString()}</div>
          </div>
        </Panel>

        <Panel title="Quick Actions">
          <div className="space-y-2">
            {[
              ["homepage", "Edit Homepage"], ["messages", "View Messages"], ["faq", "Manage FAQs"], ["media", "Upload Media"], ["backup", "Backup Data"],
            ].map(([k, l]) => (
              <button key={k} onClick={() => onNavigate(k)} className="w-full flex items-center justify-between px-3 py-2 rounded-xl border border-border hover:border-blue-300 hover:bg-blue-50/50 dark:hover:bg-blue-950/20 transition-colors text-left">
                <span className="text-sm font-medium">{l}</span>
                <span className="text-blue-600 text-xs">→</span>
              </button>
            ))}
          </div>
        </Panel>
      </div>

      <Panel title="Recent Activity" subtitle="Latest admin actions" actions={<button onClick={() => onNavigate("activity")} className="text-xs text-blue-600 hover:underline">View all →</button>}>
        {stats.recent_activity.length === 0 && <div className="text-sm text-muted-foreground py-4">No activity yet.</div>}
        <ul className="space-y-2">
          {stats.recent_activity.map((a) => (
            <li key={a.id} className="flex items-center gap-3 text-sm">
              <span className="w-2 h-2 rounded-full bg-blue-500" />
              <span className="font-medium capitalize">{a.action.replace(/_/g, " ")}</span>
              <span className="text-muted-foreground text-xs">by {a.admin_email}</span>
              <span className="ml-auto text-[10px] text-muted-foreground">{new Date(a.created_at).toLocaleString()}</span>
            </li>
          ))}
        </ul>
      </Panel>
    </div>
  );
}
